import React, { useState, useRef, FormEvent, useEffect } from 'react';
import ThemeDropdown from '@base/lib/ThemeDropdown';
import styles from './index.module.less';
import cn from 'classnames';
import Iconfont from '@base/lib/Iconfont';
import { RouteEnum } from 'pro/config/routers';
import SearchSuggest from '@base/snippets/SearchSuggest';
import useSearchSuggest from '@base/snippets/SearchSuggest/useSearchSuggest';
import FullMask from '@base/lib/FullMask';
import { getMenuMaxHeight } from 'base/utils/dropdown';
import { trackElement } from 'base/utils/tracker';
import { useRouter } from 'next/router';
import { useStoreState } from 'base/store';
import getCanonicalUrl from 'base/utils/getCanonicalUrl';
import { addClass, removeClass } from 'ava-util/dist/dom-class';
import site from 'pro/config/site';
import { useTrans } from 'base/utils/i18n';

type HeaderSearchProps = {
    className: string;
};

const MODULE_ID = 'WEB search bar';

const HeaderSearch: React.FC<HeaderSearchProps> = ({ className }) => {
    const searchSuggest = useSearchSuggest();
    const { onSearch } = searchSuggest;
    const { t } = useTrans();
    const inputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLInputElement>(null);
    const btnClearRef = useRef<HTMLButtonElement>(null);
    const [visible, setVisible] = useState<boolean>(false);
    const [menuHeight, setMenuHeight] = useState<string | undefined>();
    const { query, queryKeys } = useStoreState('rootStore');
    const { asPath, pathname } = useRouter();
    const canonicalUrl = getCanonicalUrl(asPath.replace(/\?.*$/, ''), query, queryKeys);
    const moduleTrackData = {
        relTarget: canonicalUrl,
        module: MODULE_ID,
        targetType: 'searchterm',
    };

    const handleVisible = (visible: boolean) => {
        setVisible(visible);
        if (visible) {
            setMenuHeight(getMenuMaxHeight(triggerRef.current as HTMLDivElement));
            trackElement({
                action: 'impr',
                target: canonicalUrl,
                position: '',
                ...moduleTrackData
            });
        }
    };
    const open = () => handleVisible(true);
    const close = () => handleVisible(false);
    const getContainer = () => triggerRef.current as HTMLDivElement;

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const value = getInput().value.trim();
        if (value) {
            onSearch(value);
            inputRef?.current?.blur();
            close();
        } else {
            inputRef.current?.focus();
        }
    };

    const getInput = () => inputRef.current as HTMLInputElement;
    const getBtnClear = () => btnClearRef.current as HTMLButtonElement;

    const handleInputChange = (e) => {
        const value = e.target.value.trim();
        const btnClear = getBtnClear();
        if (value.length > 0) {
            addClass(btnClear, 'g-active', false);
        } else {
            removeClass(btnClear, 'g-active', false);
        }
    };

    useEffect(() => {
        // 页面初始化在此处赋值，页面跳转后在 base/_app.tsx 文件 routeChangeStart 中赋值
        const q = pathname === RouteEnum.SEARCH ? (query.q as string || '') : '';
        getInput().value = q;
        if (q) {
            addClass(getBtnClear(), 'g-active', false);
        } else {
            removeClass(getBtnClear(), 'g-active', false);
        }
    }, []);

    return (
        <div className={className} style={{ zIndex: visible ? 1053 : 0 }}>
            <ThemeDropdown
                align={{
                    offset: [0, 0],
                    overflow: { adjustX: false, adjustY: false }
                }}
                trigger={['click']}
                open={visible}
                onOpenChange={handleVisible}
                placement="bottom"
                getPopupContainer={getContainer}
                content={
                    <SearchSuggest
                        searchSuggest={searchSuggest}
                        onSearchCallBack={close}
                        moduleTrackData={moduleTrackData}
                    />
                }
                overlayClassName={styles.menu}
                overlayStyle={{ maxHeight: menuHeight }}
                transitionName={'ant-fade'}
                showArrow={false}
            >
                <div ref={triggerRef} className={cn('search-wrap', styles['search-wrap'])}>
                    <div
                        className={cn('bg', styles.bg)}
                        onClick={e => e.stopPropagation()}
                    >
                        <form
                            className={cn('form', styles.form)}
                            onSubmit={handleSubmit}
                            method="get"
                            action={RouteEnum.SEARCH}
                        >
                            <button
                                className={cn(styles['btn-search'], 'btn-no-style')}
                                type="submit"
                            >
                                <Iconfont className="icon-search"/>
                            </button>

                            <input
                                onChange={handleInputChange}
                                autoComplete={'off'}
                                ref={inputRef}
                                type="text"
                                className={styles.input}
                                placeholder={t('web_search_placeholder', { brand: site.brand })}
                                name="q"
                                onFocus={open}
                                id="js-search-input"
                            />

                            <button
                                ref={btnClearRef}
                                className={cn(styles['btn-clear'], 'btn-no-style',)}
                                id="js-search-clear"
                                type="button"
                                onClick={(e) => {
                                    const input = getInput();
                                    input.value = '';
                                    input.focus();
                                    removeClass(getBtnClear(), 'g-active', false);
                                }}
                            >
                                <Iconfont className="icon-close-translucent"/>
                            </button>
                        </form>
                    </div>
                    <FullMask
                        visible={visible}
                        container={getContainer}
                        onClick={e => {
                            e.stopPropagation();
                            close();
                        }}
                        style={{ background: 'rgba(0,0,0,.1)' }}
                    />
                </div>
            </ThemeDropdown>
        </div>
    );
}

export default HeaderSearch;
